import { useMatch } from 'react-router-dom';

/** A matched deal route: which deal, and the sub-page suffix within it. */
export type DealRouteMatch = {
  implementationId: string;
  /**
   * '' Overview (incl. a period-scoped Overview), '/deliverables' the period list,
   * '/settings' the Settings hub (+ its sub-sections).
   */
  subPath: string;
};

/**
 * Detects whether the current location is a deal-scoped route
 * (`/deals/:implementationId`, `/periods/:reportingPeriod`, `/deliverables`, or anywhere
 * under `/settings`) and, if so, returns the deal id + sub-page suffix. Used by
 * the portal templates to swap the flat portal nav for the deal-scoped sidebar.
 * Returns null off deal routes.
 *
 * Two collapses, both so the deal switcher lands somewhere valid in the *other*
 * deal:
 * - The Settings hub owns nested sub-routes (`/settings/report-config`, etc.) →
 *   all collapse to `/settings` (the hub redirects to its default section).
 * - A period-scoped Overview collapses to Overview: each deal has its own
 *   schedule, so the other deal may not have that period.
 *
 * `useMatch` (not `useParams`) because the template renders outside the
 * `<Routes>` tree, where route params are not in scope.
 */
export function useDealRouteMatch(): DealRouteMatch | null {
  const overviewMatch = useMatch('/deals/:implementationId');
  const periodMatch = useMatch(
    '/deals/:implementationId/periods/:reportingPeriod',
  );
  // Legacy cycle path: still matched so the nav renders during the redirect.
  const cycleMatch = useMatch('/deals/:implementationId/cycles/:cycleId');
  const deliverablesMatch = useMatch('/deals/:implementationId/deliverables');
  // Legacy `/files` path: still matched so the nav renders during the redirect.
  const filesMatch = useMatch('/deals/:implementationId/files');
  const settingsMatch = useMatch('/deals/:implementationId/settings/*');
  const match =
    overviewMatch ??
    periodMatch ??
    cycleMatch ??
    deliverablesMatch ??
    filesMatch ??
    settingsMatch;
  const implementationId = match?.params.implementationId;
  if (!implementationId) return null;
  let subPath = '';
  if (deliverablesMatch || filesMatch) subPath = '/deliverables';
  else if (settingsMatch) subPath = '/settings';
  return { implementationId, subPath };
}
